
export default {
  name: 'UIDonutSvgMask',
  props: {
    series: {
      type: Array,
      required: true,
    },
    colors: {
      type: Array,
      default: () => [],
    },
    width: {
      type: Number,
      default: 64,
    },
    height: {
      type: Number,
      default: 64,
    },
    strokeWidth: {
      type: Number,
      default: 12,
    },
    animated: {
      type: Boolean,
      default: false,
    },
    animationDurationMs: {
      type: Number,
      default: 900,
    },
  },
  data() {
    return {
      animationProgress: this.animated ? 0 : 1,
      animationFrameId: null,
      maskId: `ui-donut-mask-${this._uid}`,
    };
  },
  computed: {
    sanitizedSeries() {
      return this.series.map((value) => {
        const numericValue = Number(value);
        return Number.isFinite(numericValue) && numericValue > 0
          ? numericValue
          : 0;
      });
    },

    centerX() {
      return this.width / 2;
    },

    centerY() {
      return this.height / 2;
    },

    radius() {
      const minDimension = Math.min(this.width, this.height);
      return Math.max((minDimension - this.strokeWidth) / 2, 0);
    },

    circumference() {
      return 2 * Math.PI * this.radius;
    },

    maskStrokeWidth() {
      return this.strokeWidth + 2;
    },

    segments() {
      if (!this.sanitizedSeries.length || this.circumference <= 0) {
        return [];
      }

      let accumulatedPercent = 0;

      return this.sanitizedSeries.map((percent, index) => {
        const segmentLength = (percent / 100) * this.circumference;
        const gapLength = Math.max(this.circumference - segmentLength, 0);
        const dashOffset = -((accumulatedPercent / 100) * this.circumference);

        accumulatedPercent += percent;

        return {
          color: this.colors[index % this.colors.length] || '#000000',
          dashArray: `${segmentLength} ${gapLength}`,
          dashOffset,
        };
      });
    },

    easedAnimationProgress() {
      const t = this.animationProgress;
      return 1 - Math.pow(1 - t, 3);
    },

    revealAngle() {
      return -90 + this.easedAnimationProgress * 360;
    },

    isFullyRevealed() {
      return this.easedAnimationProgress >= 0.999;
    },

    revealArcPath() {
      if (
        !this.animated ||
        this.easedAnimationProgress <= 0 ||
        this.radius <= 0
      ) {
        return '';
      }

      if (this.isFullyRevealed) {
        return '';
      }

      return this.describeArcPath(-90, this.revealAngle);
    },
  },
  mounted() {
    if (!this.animated) {
      return;
    }

    this.$nextTick(() => {
      this.startRevealAnimation();
    });
  },
  beforeDestroy() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
  },
  methods: {
    startRevealAnimation() {
      const duration = Math.max(Number(this.animationDurationMs) || 0, 0);

      if (duration === 0) {
        this.animationProgress = 1;
        return;
      }

      const startTime = performance.now();

      const tick = (now) => {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);

        this.animationProgress = progress;

        if (progress < 1) {
          this.animationFrameId = requestAnimationFrame(tick);
        } else {
          this.animationFrameId = null;
        }
      };

      this.animationFrameId = requestAnimationFrame(tick);
    },

    polarToCartesian(angleInDegrees, radius = this.radius) {
      const angleInRadians = (angleInDegrees * Math.PI) / 180;

      return {
        x: this.centerX + radius * Math.cos(angleInRadians),
        y: this.centerY + radius * Math.sin(angleInRadians),
      };
    },

    describeArcPath(startAngle, endAngle) {
      const start = this.polarToCartesian(startAngle);
      const end = this.polarToCartesian(endAngle);
      const angleDelta = endAngle - startAngle;
      const largeArcFlag = angleDelta > 180 ? 1 : 0;

      return [
        `M ${start.x} ${start.y}`,
        `A ${this.radius} ${this.radius} 0 ${largeArcFlag} 1 ${end.x} ${end.y}`,
      ].join(' ');
    },
  },
};
